/**
 * API client with 401 interceptor and backend-unavailable retry — AC1, AC2.
 *
 * Two exports:
 *
 * 1. `createApiClient()` — factory used by the attempt-engine (mid-attempt
 *    calls). Preserves existing behaviour: on 401, attempt routes dispatch
 *    'bimassess:session-expired' (ReauthModal), non-attempt routes redirect to
 *    /sign-in. No retry — the attempt engine manages its own retry loop.
 *
 * 2. `apiFetch` — singleton replacement for raw `fetch()` on dashboard/admin/
 *    company/me pages. Adds on top of the 401 interceptor:
 *    - Retry on network errors and 503 (backend cold-start) with exponential
 *      back-off (2 retries: 500 ms, 1 500 ms). Does NOT retry 4xx (auth, bad
 *      request) — those are definitive.
 *    - On 401: same logic as `createApiClient` (redirect or session-expired
 *      event depending on current route).
 *
 * Rationale: the cleanest global 401 + retry coverage without TanStack Query is
 * routing every authenticated `fetch()` through this shared wrapper. Per-page
 * raw fetch calls are replaced with `apiFetch` so the fix is in one place and
 * does not require per-page changes to error handling logic.
 */

// ─── Shared helpers ───────────────────────────────────────────────────────────

function isAttemptRoute(): boolean {
  const pathname = window.location.pathname;
  return pathname.startsWith('/take/') || pathname.startsWith('/attempt/');
}

/**
 * True when the current page is a public auth page that does not require a
 * session. 401s on these pages are expected (e.g. StaleConsentModal probing
 * /api/me/consent-status on every page load, including sign-in) and must NOT
 * trigger a redirect loop.
 *
 * Marketing pages (/, /business, /individual, /pricing, /catalogue, /legal/*)
 * are also public: anonymous visitors must be able to reach them without being
 * bounced to /sign-in. The auth-aware landing page probes /api/me silently; a
 * 401 there means "not logged in — show marketing normally".
 */
function isPublicAuthPage(): boolean {
  const pathname = window.location.pathname;
  return (
    pathname === '/' ||
    pathname.startsWith('/sign-in') ||
    pathname.startsWith('/sign-up') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/business') ||
    pathname.startsWith('/individual') ||
    pathname.startsWith('/pricing') ||
    pathname.startsWith('/catalogue') ||
    pathname.startsWith('/legal/')
  );
}

function handle401(): void {
  // Never redirect if already on a public auth page — prevents redirect loops
  // caused by unauthenticated background probes (e.g. StaleConsentModal).
  if (isPublicAuthPage()) return;

  if (isAttemptRoute()) {
    // AC2: mid-attempt route — dispatch event for ReauthModal, no navigation
    window.dispatchEvent(new CustomEvent('bimassess:session-expired'));
  } else {
    // AC1: non-attempt route — redirect to sign-in with return_to
    window.location.href =
      '/sign-in?return_to=' + encodeURIComponent(window.location.pathname);
  }
}

/** True when the response status represents a transient backend failure worth retrying. */
function isRetryable(status: number): boolean {
  return status === 503 || status === 502 || status === 504;
}

// ─── 1. createApiClient — backward-compat for attempt-engine ─────────────────

export function createApiClient(): (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response> {
  return async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const response = await fetch(input, init);

    if (response.status === 401) {
      handle401();
    }

    return response;
  };
}

// ─── 2. apiFetch — global authenticated fetch for all non-attempt pages ───────

/**
 * Retry delays in ms (two retries: ~500 ms, then ~1 500 ms). Render free-tier
 * typically wakes within a few seconds; two retries cover the cold-start window
 * without blocking the UI too long.
 */
const RETRY_DELAYS_MS = [500, 1500];

/**
 * Global authenticated fetch with 401-redirect and cold-start retry.
 *
 * Drop-in replacement for `fetch()` on any authenticated page. The caller's
 * error handling is unchanged — on a definitive failure the response (or
 * thrown error after all retries) surfaces normally.
 *
 * Callers that show a "Reconnecting…" UI: wrap the call in try/catch to catch
 * the network-error throw after all retries are exhausted, OR check response.ok.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let lastError: unknown;
  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    // Back-off before retrying (not before the first attempt).
    if (attempt > 0) {
      const delay = RETRY_DELAYS_MS[attempt - 1] ?? 1500;
      await new Promise<void>((resolve) => setTimeout(resolve, delay));
    }

    try {
      const response = await fetch(input, init);

      // 401 is definitive — handle immediately, do not retry.
      if (response.status === 401) {
        handle401();
        return response;
      }

      // Retryable server-side failures (backend waking up): try again.
      if (isRetryable(response.status) && attempt < RETRY_DELAYS_MS.length) {
        lastResponse = response;
        continue;
      }

      // Any other response (200, 400, 403, 404, 500 …) is returned as-is.
      return response;
    } catch (err) {
      // Network-level failure (fetch threw — backend completely unreachable).
      lastError = err;
      if (attempt < RETRY_DELAYS_MS.length) {
        // Will retry
        continue;
      }
    }
  }

  // All retries exhausted.
  if (lastError !== undefined) {
    throw lastError;
  }
  // Exhausted on retryable HTTP status — return the last response so the
  // caller's .ok check surfaces the error.
  return lastResponse!;
}
